import React from 'react';
import { ptIfSet, ptOfListItem, ptOfRepeaterItem, ptOfSlider } from './calc.js';
import { Btn, ChipGroup, Collapsible, ConsultToggle, Field, KiBanner, PtBadge, PtSliderMeta, RadioList, Segmented, Slider, TextInput } from './components.jsx';
import { t } from './i18n.js';
const { useState } = React;

const _ptMapForOptions2 = (state, path, optionIds) => {
  const map = {};
  for (const id of optionIds) map[id] = ptIfSet(state, path, id);
  return map;
};

const Page2 = ({ state, setState }) => {
  const { p2 } = state;
  const setP2 = (patch) => setState(s => ({ ...s, p2: { ...s.p2, ...patch } }));
  const toggle = (key, id) => {
    const arr = p2[key] || [];
    setP2({ [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };
  const setUnknown = (field, on) => {
    const u = { ...(p2._unknown || {}) };
    if (on) u[field] = true; else delete u[field];
    setP2({ _unknown: u });
  };
  const isUnknown = (f) => !!(p2._unknown && p2._unknown[f]);
  const setConsult = (n, on) => {
    const c = { ...(p2._consult || {}) };
    if (on) c[n] = true; else delete c[n];
    setP2({ _consult: c });
  };
  const isConsult = (n) => !!(p2._consult && p2._consult[n]);
  const consultProps = (n) => ({
    consultActive: isConsult(n),
    headAction: <ConsultToggle active={isConsult(n)} onClick={() => setConsult(n, !isConsult(n))}/>,
  });

  // Integrations repeater
  const addIntegration = () => setP2({ integrations: [...p2.integrations, { id: Date.now(), system: '', direction: 'bidirectional', mech: 'rest', criticality: 'wichtig' }] });
  const updateIntegration = (id, patch) => setP2({ integrations: p2.integrations.map(i => i.id === id ? { ...i, ...patch } : i) });
  const removeIntegration = (id) => setP2({ integrations: p2.integrations.filter(i => i.id !== id) });
  const dismissKiBanner = () => setState(s => ({ ...s, ki_metadata: { ...(s.ki_metadata || {}), bannerDismissed: true } }));

  // Helper: build security/compliance options with localised label + help
  const securityOptions = ['mfa','sso','rbac','abac','audit','encryption','pen','waf','pii-mask'].map(id => ({
    id,
    icon: { mfa: 'shield-halved', sso: 'right-to-bracket', rbac: 'user-lock', abac: 'fingerprint', audit: 'clipboard-check', encryption: 'lock', pen: 'user-secret', waf: 'tower-broadcast', 'pii-mask': 'mask' }[id],
    label: t(`p2.security_options.${id}.label`),
    help: t(`p2.security_options.${id}.help`),
  }));
  const complianceOptions = ['revdsg','dsgvo','iso27001','nis2','soc2','branchen','a11y'].map(id => ({
    id,
    icon: { revdsg: 'flag', dsgvo: 'flag', iso27001: 'shield', nis2: 'shield-halved', soc2: 'file-shield', branchen: 'industry', a11y: 'universal-access' }[id],
    label: t(`p2.compliance_options.${id}.label`),
    help: t(`p2.compliance_options.${id}.help`),
  }));
  const dataDomainOptions = ['kunden','produkte','transaktionen','finanzen','gesundheit','mitarbeiter','verträge','inhalte','iot'].map(id => ({
    id,
    icon: { kunden: 'address-book', produkte: 'box', transaktionen: 'arrow-right-arrow-left', finanzen: 'coins', gesundheit: 'heart-pulse', mitarbeiter: 'id-card', verträge: 'file-contract', inhalte: 'file-lines', iot: 'wifi' }[id],
    label: t(`p2.data_domains.${id}`),
  }));

  // ── Calc-Overlay: Marginal-PT pro Option/Item ─────────────────────────────
  const ptMigration       = _ptMapForOptions2(state, 'p2.migration',          ['no','yes','unknown']);
  const ptMigStreams      = ptOfSlider(state, 'p2.migrationStreams');
  const ptSensitivity     = _ptMapForOptions2(state, 'p2.sensitivity',        ['public','internal','personal','sensitive']);
  const ptActiveUsers     = _ptMapForOptions2(state, 'p2.activeUsers',        ['small','medium','large','huge']);
  const ptPeak            = _ptMapForOptions2(state, 'p2.peakConcurrency',    ['50','500','5k','5k+']);
  const ptTenant          = _ptMapForOptions2(state, 'p2.tenantModel',        ['single','multi-mid','multi-many']);
  const ptResidency       = _ptMapForOptions2(state, 'p2.dataResidency',      ['ch','eu','global']);
  const ptAvailability    = _ptMapForOptions2(state, 'p2.availability',       ['standard','low-downtime','full-ha']);
  const ptDomainMap       = {}; dataDomainOptions.forEach(o => { ptDomainMap[o.id] = ptOfListItem(state, 'p2.dataDomains', o.id); });
  const ptSecurityMap     = {}; securityOptions.forEach(o => { ptSecurityMap[o.id] = ptOfListItem(state, 'p2.security', o.id); });
  const ptComplianceMap   = {}; complianceOptions.forEach(o => { ptComplianceMap[o.id] = ptOfListItem(state, 'p2.compliance', o.id); });

  const migSummary = p2.migration
    ? ({ yes: t('p2.section_migration_summary_yes'), no: t('p2.section_migration_summary_no'), unknown: t('p2.section_migration_summary_unknown') })[p2.migration]
    : t('p2.section_migration_summary_open');

  const sensSummary = ({
    public:    t('p2.sensitivity_public_label'),
    internal:  t('p2.sensitivity_internal_label'),
    personal:  t('p2.sensitivity_personal_label'),
    sensitive: t('p2.sensitivity_sensitive_label'),
  })[p2.sensitivity] || '—';

  const userSummary = p2.activeUsers ? ({
    small:  t('p2.users_small_label'),
    medium: t('p2.users_medium_label'),
    large:  t('p2.users_large_label'),
    huge:   t('p2.users_huge_label'),
  })[p2.activeUsers] : t('p2.section_availability_summary_open');

  const residencySummary = ({
    ch:     t('p2.compliance_residency_ch'),
    eu:     t('p2.compliance_residency_eu'),
    global: t('p2.compliance_residency_global'),
  })[p2.dataResidency] || '—';

  const availSummary = p2.availability ? ({
    standard:       t('p2.section_availability_summary_standard'),
    redundant:      t('p2.section_availability_summary_redundant'),
    'low-downtime': t('p2.section_availability_summary_low'),
    'full-ha':      t('p2.section_availability_summary_full'),
  })[p2.availability] : t('p2.section_availability_summary_open');

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <KiBanner state={state} onDismiss={dismissKiBanner}/>
      <header style={{ marginBottom: 24 }}>
        <span className="block__cat">{t('p2.section_label')}</span>
        <h1 className="block__title" style={{ fontSize: 24, marginTop: 6 }}>{t('p2.headline')}</h1>
      </header>

      {/* === 1: Integrationen === */}
      <Collapsible num={1} title={t('p2.section_integrations_title')} defaultOpen {...consultProps(1)}
        summary={p2.integrations.length ? t('p2.section_integrations_summary_some', { count: p2.integrations.length }) : t('p2.section_integrations_summary_none')}
      >
        <div className="mod-ai-banner" style={{ marginBottom: 16 }}>
          <i className="fa-solid fa-circle-info"></i>
          <span>{t('p2.section_integrations_lead')}</span>
        </div>
        <div className="repeater">
          {p2.integrations.map((it, idx) => (
            <div key={it.id} className="repeater-item">
              <div className="repeater-item__head">
                <span>
                  {t('p2.integration_n', { n: idx + 1 })}
                  <PtBadge value={ptOfRepeaterItem(state, 'p2.integrations', (i) => i.id === it.id)} dim/>
                </span>
                <Btn kind="ghost" size="xs" variant="icon" icon="trash" onClick={() => removeIntegration(it.id)} aria-label={t('common.remove')}/>
              </div>
              <div className="grid-2">
                <Field label={t('p2.integration_field_system')} hint={t('p2.integration_field_system_hint')}>
                  <TextInput
                    value={it.system}
                    onChange={(v) => updateIntegration(it.id, { system: v })}
                    placeholder={t('p2.integration_field_system_placeholder')}
                    maxLength={100}
                  />
                </Field>
                <Field label={t('p2.integration_field_direction')}>
                  <Segmented size="sm"
                    options={[
                      { id: 'read',          label: t('p2.integration_dir_read') },
                      { id: 'write',         label: t('p2.integration_dir_write') },
                      { id: 'bidirectional', label: t('p2.integration_dir_bidirectional') },
                    ]}
                    value={it.direction} onChange={(v) => updateIntegration(it.id, { direction: v })}
                  />
                </Field>
                <Field label={t('p2.integration_field_mech')}>
                  <Segmented size="sm"
                    options={[
                      { id: 'rest',  label: t('p2.integration_mech_rest') },
                      { id: 'soap',  label: t('p2.integration_mech_soap') },
                      { id: 'file',  label: t('p2.integration_mech_file') },
                      { id: 'event', label: t('p2.integration_mech_event') },
                    ]}
                    value={it.mech} onChange={(v) => updateIntegration(it.id, { mech: v })}
                  />
                </Field>
                <Field label={t('p2.integration_field_criticality')} hint={t('p2.integration_criticality_hint')}>
                  <Segmented size="sm"
                    options={[
                      { id: 'nice-to-have', label: t('p2.integration_crit_nth') },
                      { id: 'wichtig',      label: t('p2.integration_crit_wichtig') },
                      { id: 'kritisch',     label: t('p2.integration_crit_kritisch') },
                    ]}
                    value={it.criticality} onChange={(v) => updateIntegration(it.id, { criticality: v })}
                  />
                </Field>
              </div>
            </div>
          ))}
          <button type="button" className="repeater-add" onClick={addIntegration}>
            <i className="fa-solid fa-plus"></i>
            {p2.integrations.length === 0 ? t('p2.integration_add_first') : t('p2.integration_add_more')}
          </button>
        </div>
      </Collapsible>

      {/* === 2: Datenmigration === */}
      <Collapsible num={2} title={t('p2.section_migration_title')} {...consultProps(2)} summary={migSummary}>
        <Field label={t('p2.migration_label')}>
          <Segmented
            options={[
              { id: 'no',      label: t('p2.migration_no') },
              { id: 'yes',     label: t('p2.migration_yes') },
              { id: 'unknown', label: t('p2.migration_unknown') },
            ]}
            value={p2.migration}
            onChange={(v) => setP2({ migration: v })}
            ptValues={ptMigration}
          />
        </Field>
        {p2.migration === 'yes' && (
          <div className="stack-md" style={{ marginTop: 16 }}>
            <Field label={t('p2.migration_streams_label')} hint={t('p2.migration_streams_hint')}>
              <Slider value={p2.migrationStreams} onChange={(v) => setP2({ migrationStreams: v })} min={0} max={20}/>
              <PtSliderMeta total={ptMigStreams.total} perUnit={ptMigStreams.perUnit}/>
            </Field>
            <Field label={t('p2.migration_quality_label')}>
              <Segmented
                options={[
                  { id: 'high',  label: t('p2.migration_quality_high_label'),  sub: t('p2.migration_quality_high_sub') },
                  { id: 'mixed', label: t('p2.migration_quality_mixed_label'), sub: t('p2.migration_quality_mixed_sub') },
                  { id: 'low',   label: t('p2.migration_quality_low_label'),   sub: t('p2.migration_quality_low_sub') },
                ]}
                value={p2.dataQuality}
                onChange={(v) => setP2({ dataQuality: v })}
                stack
              />
            </Field>
            <Field label={t('p2.migration_path_label')}>
              <Segmented
                options={[
                  { id: 'bigbang',  label: t('p2.migration_path_bigbang_label'),  sub: t('p2.migration_path_bigbang_sub') },
                  { id: 'phased',   label: t('p2.migration_path_phased_label'),   sub: t('p2.migration_path_phased_sub') },
                  { id: 'parallel', label: t('p2.migration_path_parallel_label'), sub: t('p2.migration_path_parallel_sub') },
                ]}
                value={p2.migrationPath}
                onChange={(v) => setP2({ migrationPath: v })}
                stack
              />
            </Field>
          </div>
        )}
      </Collapsible>

      {/* === 3: Daten === */}
      <Collapsible num={3} title={t('p2.section_data_title')} {...consultProps(3)}
        summary={`${p2.dataDomains.length} · ${sensSummary}`}>
        <Field label={t('p2.data_domains_label')} hint={t('p2.data_domains_hint')}>
          <ChipGroup value={p2.dataDomains} onChange={(v) => setP2({ dataDomains: v })} options={dataDomainOptions} ptValues={ptDomainMap}/>
        </Field>
        <Field label={t('p2.sensitivity_label')} hint={t('p2.sensitivity_hint')}>
          <Segmented
            options={[
              { id: 'public',    label: t('p2.sensitivity_public_label'),    sub: t('p2.sensitivity_public_sub') },
              { id: 'internal',  label: t('p2.sensitivity_internal_label'),  sub: t('p2.sensitivity_internal_sub') },
              { id: 'personal',  label: t('p2.sensitivity_personal_label'),  sub: t('p2.sensitivity_personal_sub') },
              { id: 'sensitive', label: t('p2.sensitivity_sensitive_label'), sub: t('p2.sensitivity_sensitive_sub') },
            ]}
            value={p2.sensitivity}
            onChange={(v) => setP2({ sensitivity: v })}
            stack
            ptValues={ptSensitivity}
          />
        </Field>
      </Collapsible>

      {/* === 4: Nutzer / Skalierung === */}
      <Collapsible num={4} title={t('p2.section_users_title')} {...consultProps(4)} summary={userSummary}>
        <Field label={t('p2.users_label')}>
          <div className="between" style={{ alignItems: 'flex-start', gap: 16 }}>
            <Segmented stack
              options={[
                { id: 'small',  label: t('p2.users_small_label'),  sub: t('p2.users_small_sub') },
                { id: 'medium', label: t('p2.users_medium_label'), sub: t('p2.users_medium_sub') },
                { id: 'large',  label: t('p2.users_large_label'),  sub: t('p2.users_large_sub') },
                { id: 'huge',   label: t('p2.users_huge_label'),   sub: t('p2.users_huge_sub') },
              ]}
              value={p2.activeUsers}
              onChange={(v) => setP2({ activeUsers: v })}
              ptValues={ptActiveUsers}
            />
          </div>
        </Field>
        <Field label={t('p2.peak_label')} hint={t('p2.peak_hint')}>
          <Segmented
            options={[
              { id: '50',   label: '< 50' },
              { id: '500',  label: '< 500' },
              { id: '5k',   label: '< 5’000' },
              { id: '5k+',  label: '≥ 5’000' },
            ]}
            value={p2.peakConcurrency}
            onChange={(v) => setP2({ peakConcurrency: v })}
            ptValues={ptPeak}
          />
        </Field>
        <Field label={t('p2.tenant_label')}>
          <RadioList
            value={p2.tenantModel}
            onChange={(v) => setP2({ tenantModel: v })}
            options={[
              { id: 'single',     title: t('p2.tenant_single_title'), sub: t('p2.tenant_single_sub') },
              { id: 'multi-mid',  title: t('p2.tenant_mid_title'),    sub: t('p2.tenant_mid_sub') },
              { id: 'multi-many', title: t('p2.tenant_many_title'),   sub: t('p2.tenant_many_sub') },
            ]}
            ptValues={ptTenant}
          />
        </Field>
      </Collapsible>

      {/* === 5: Sicherheit === */}
      <Collapsible num={5} title={t('p2.section_security_title')} {...consultProps(5)}
        summary={t('p2.security_count_summary', { count: p2.security.length })}>
        <Field label={t('p2.security_label')} hint={t('p2.security_hint')}>
          <ChipGroup value={p2.security} onChange={(v) => setP2({ security: v })} options={securityOptions} ptValues={ptSecurityMap}/>
        </Field>
      </Collapsible>

      {/* === 6: Compliance === */}
      <Collapsible num={6} title={t('p2.section_compliance_title')} {...consultProps(6)}
        summary={t('p2.compliance_count_summary', { count: p2.compliance.length, residency: residencySummary })}>
        <Field label={t('p2.compliance_label')} hint={t('p2.compliance_hint')}>
          <ChipGroup value={p2.compliance} onChange={(v) => setP2({ compliance: v })} options={complianceOptions} ptValues={ptComplianceMap}/>
        </Field>
        <Field label={t('p2.data_residency_label')}>
          <Segmented
            options={[
              { id: 'ch',     label: t('p2.data_residency_ch_label'),     sub: t('p2.data_residency_ch_sub') },
              { id: 'eu',     label: t('p2.data_residency_eu_label'),     sub: t('p2.data_residency_eu_sub') },
              { id: 'global', label: t('p2.data_residency_global_label'), sub: t('p2.data_residency_global_sub') },
            ]}
            value={p2.dataResidency}
            onChange={(v) => setP2({ dataResidency: v })}
            stack
            ptValues={ptResidency}
          />
        </Field>
      </Collapsible>

      {/* === 7: Verfügbarkeit === */}
      <Collapsible num={7} title={t('p2.section_availability_title')} {...consultProps(7)} summary={availSummary}>
        <Field label={t('p2.availability_label')} hint={t('p2.availability_hint')}>
          <RadioList
            value={p2.availability}
            onChange={(v) => setP2({ availability: v })}
            options={[
              { id: 'standard',     title: t('p2.availability_standard_title'), sub: t('p2.availability_standard_sub') },
              { id: 'low-downtime', title: t('p2.availability_low_title'),      sub: t('p2.availability_low_sub') },
              { id: 'full-ha',      title: t('p2.availability_full_title'),     sub: t('p2.availability_full_sub') },
            ]}
            ptValues={ptAvailability}
          />
        </Field>
      </Collapsible>
    </div>
  );
};

export { Page2 };
